import { z } from 'zod';

// SSN format: XXX-XX-XXXX (3 digits, dash, 2 digits, dash, 4 digits)
const SSN_PATTERN = /^\d{3}-\d{2}-\d{4}$/;

// Phone format: X{1-3}-X{1-3}-X{1-4} (1-3 digits, dash, 1-3 digits, dash, 1-4 digits)
const PHONE_PATTERN = /^\d{1,3}-\d{1,3}-\d{1,4}$/;

// ZIP code format: 5 digits optionally followed by dash and 4 digits (ZIP+4)
const ZIP_PATTERN = /^\d{5}(-\d{4})?$/;

export const createPatientFormSchema = z
  .object({
    // Kinnser Information (16 fields)
    kinnser_clinic_id: z
      .string()
      .nullable()
      .optional()
      .refine(val => val && val.length >= 1, 'Clinic is required'),
    kinnser_physician_id: z
      .string()
      .nullable()
      .optional()
      .refine(val => val && val.length >= 1, 'Physician is required'),
    kinnser_branch_id: z
      .string()
      .nullable()
      .optional()
      .refine(val => val && val.length >= 1, 'Branch is required'),
    kinnser_insurance_provider_id: z
      .string()
      .nullable()
      .optional()
      .refine(val => val && val.length >= 1, 'Insurance Provider is required'),
    kinnser_admission_type: z
      .string()
      .nullable()
      .optional()
      .refine(val => val && val.length >= 1, 'Admission Type is required'),
    kinnser_hcpcs_code_key: z
      .string()
      .nullable()
      .optional()
      .refine(val => val && val.length >= 1, 'HCPCS Code is required'),
    kinnser_mrn: z.string().nullable().optional(),
    kinnser_hh_cahps_opt_out: z.boolean().nullable().optional(),
    kinnser_language_key: z
      .string()
      .nullable()
      .optional()
      .refine(val => val && val.length >= 1, 'Language is required'),
    kinnser_other_language: z.string().nullable().optional(),
    kinnser_create_episode: z.boolean().nullable().optional(),

    // Push to EMR flags
    push_to_kinnser: z.boolean().nullable().optional(),
    push_to_roger: z.boolean().nullable().optional(),

    // Roger Episode fields
    roger_type_of_soc: z.string().nullable().optional(),
    roger_create_episode: z.boolean().nullable().optional(),

    // Episode and SOC fields
    kinnser_type_of_soc: z.string().nullable().optional(),
    kinnser_soc_date: z.string().nullable().optional(),
    kinnser_episode_start_date: z.string().nullable().optional(),

    // Clinician assignment fields
    kinnser_clinician_id: z.string().nullable().optional(),
    kinnser_clinical_care_manager_id: z.string().nullable().optional(),
    kinnser_case_manager_id: z.string().nullable().optional(),
    kinnser_oasis_approver_id: z.string().nullable().optional(),
    kinnser_pt_approver_id: z.string().nullable().optional(),
    kinnser_ot_approver_id: z.string().nullable().optional(),
    kinnser_st_approver_id: z.string().nullable().optional(),

    // Patient Identification (8 fields)
    patient_first_name: z
      .string()
      .max(19, 'First name must be 19 characters or less')
      .nullable()
      .optional()
      .refine(val => val && val.length >= 1, 'First name is required'),
    patient_middle_name: z.string().nullable().optional(),
    patient_last_name: z
      .string()
      .max(20, 'Last name must be 20 characters or less')
      .nullable()
      .optional()
      .refine(val => val && val.length >= 1, 'Last name is required'),
    patient_suffix: z.string().nullable().optional(),
    patient_dob: z.string().nullable().optional(),
    patient_gender: z
      .string()
      .nullable()
      .optional()
      .refine(val => val && val.length >= 1, 'Gender is required'),
    patient_ssn: z
      .string()
      .refine(
        val => !val || SSN_PATTERN.test(val),
        'SSN must be in format XXX-XX-XXXX (e.g., 123-45-6789)'
      )
      .nullable()
      .optional(),
    patient_primary_language: z.string().nullable().optional(),

    // Contact Information (8 fields)
    patient_phone: z
      .string()
      .nullable()
      .optional()
      .refine(val => val && val.length >= 1, 'Phone is required')
      .refine(
        val => !val || PHONE_PATTERN.test(val),
        'Phone must be in format aaa-bbb-cccc (e.g., 555-123-4567)'
      ),
    patient_email: z
      .string()
      .refine(
        val => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
        'Invalid email format'
      )
      .nullable()
      .optional(),
    patient_street_address: z
      .string()
      .nullable()
      .optional()
      .refine(val => val && val.length >= 1, 'Address is required'),
    patient_city: z
      .string()
      .nullable()
      .optional()
      .refine(val => val && val.length >= 1, 'City is required'),
    patient_state: z
      .string()
      .nullable()
      .optional()
      .refine(val => val && val.length >= 1, 'State is required'),
    patient_zip_code: z
      .string()
      .nullable()
      .optional()
      .refine(val => val && val.length >= 1, 'Zip Code is required')
      .refine(
        val => !val || ZIP_PATTERN.test(val),
        'Zip Code must be in format 12345 or 12345-6789'
      ),
    patient_county: z.string().nullable().optional(),

    // Insurance Information
    medicaid_id: z.string().nullable().optional(),

    // Notes (1 field)
    patient_notes: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    // Other Language is required when language key is '6' (Other/Unknown)
    if (
      data.kinnser_language_key === '6' &&
      (!data.kinnser_other_language || data.kinnser_other_language.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Other Language is required when "Other/Unknown" is selected',
        path: ['kinnser_other_language'],
      });
    }

    // Episode fields are required when Create Episode is enabled
    if (data.kinnser_create_episode === true) {
      if (
        !data.kinnser_clinician_id ||
        data.kinnser_clinician_id.length === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Clinician is required',
          path: ['kinnser_clinician_id'],
        });
      }
      if (
        !data.kinnser_clinical_care_manager_id ||
        data.kinnser_clinical_care_manager_id.length === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Clinical Care Manager is required',
          path: ['kinnser_clinical_care_manager_id'],
        });
      }
      if (
        !data.kinnser_case_manager_id ||
        data.kinnser_case_manager_id.length === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Case Manager is required',
          path: ['kinnser_case_manager_id'],
        });
      }
      if (
        !data.kinnser_oasis_approver_id ||
        data.kinnser_oasis_approver_id.length === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'OASIS Approver is required',
          path: ['kinnser_oasis_approver_id'],
        });
      }
      if (
        !data.kinnser_pt_approver_id ||
        data.kinnser_pt_approver_id.length === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'PT Approver is required',
          path: ['kinnser_pt_approver_id'],
        });
      }
      if (
        !data.kinnser_ot_approver_id ||
        data.kinnser_ot_approver_id.length === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'OT Approver is required',
          path: ['kinnser_ot_approver_id'],
        });
      }
      if (
        !data.kinnser_st_approver_id ||
        data.kinnser_st_approver_id.length === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'ST Approver is required',
          path: ['kinnser_st_approver_id'],
        });
      }
      if (!data.kinnser_type_of_soc || data.kinnser_type_of_soc.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Type of SOC is required',
          path: ['kinnser_type_of_soc'],
        });
      }
      if (!data.kinnser_soc_date || data.kinnser_soc_date.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Start of Care Date is required',
          path: ['kinnser_soc_date'],
        });
      }
      if (
        !data.kinnser_episode_start_date ||
        data.kinnser_episode_start_date.length === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Episode Start Date is required',
          path: ['kinnser_episode_start_date'],
        });
      }
    }
  });

export type CreatePatientFormData = z.infer<typeof createPatientFormSchema>;
